import { ReactElement, MouseEvent, useState } from 'react';
import clsx from 'clsx';
import {
  createStyles,
  alpha,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  AppBar,
  Toolbar,
  CssBaseline,
  IconButton,
  Tooltip,
  InputBase,
  Menu,
  MenuItem,
} from '@material-ui/core';
import Zoom from '@material-ui/core/Zoom';
import IconLightTheme from '@material-ui/icons/BrightnessHigh';
import IconDarkTheme from '@material-ui/icons/Brightness4';
import IconAccountCircleOutlined from '@material-ui/icons/AccountCircleOutlined';
import IconSearch from '@material-ui/icons/Search';
import IconSettingsOutlined from '@material-ui/icons/SettingsOutlined';

// Components
import MenuIcon from '@material-ui/icons/Menu';
import PageTitle from '../../components/PageTitle/PageTitle';
import SettingsVerticalTabs from '../../components/Settings/Settings';

// Constants
import { DRAWER_WIDTH } from '../../utils/constants';

import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { onLogout } from '../../store/UserSession/UserSessionActions';
import { GetString } from '../StringResourceManager';

import { useAuth0 } from '@auth0/auth0-react';
import { ApplicationState } from '../../store';
import { IdentityManagerType } from '../../Interfaces/SSO/IdentityManagerConfiguration';
import { EnterpriseTooltip } from '../EnterpriseTooltip';

// Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    appBarShift: {
      marginLeft: DRAWER_WIDTH,
      width: `calc(100% - ${DRAWER_WIDTH}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    toolbar: {
      flex: 1,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#fff',
    },
    title: {
      flex: 1,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      color: '#23496B',
      marginRight: theme.spacing(1),
    },
    menuButton: {
      marginRight: 36,
    },
    hide: {
      display: 'none',
    },
    search: {
      position: 'relative',
      borderRadius: theme.shape.borderRadius,
      backgroundColor: alpha('#23496B', 0.15),
      '&:hover': {
        backgroundColor: alpha('#23496B', 0.25),
      },
      marginLeft: 0,
      marginRight: theme.spacing(1),
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(1),
        width: 'auto',
      },
    },
    searchIcon: {
      padding: theme.spacing(0, 2),
      height: '100%',
      position: 'absolute',
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#23496B',
    },
    inputRoot: {
      color: 'inherit',
    },
    inputInput: {
      color: '#23496B',
      padding: theme.spacing(1, 1, 1, 0),
      paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
      transition: theme.transitions.create('width'),
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: '12ch',
        '&:focus': {
          width: '20ch',
        },
      },
    },
    appBarIconButton: {
      color: '#23496B',
      '&:active': {
        outline: 'none',
      },
      '&:hover': {
        outline: 'none',
      },
      '&:focus': {
        outline: 'none',
      },
    },
    settingsModal: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: theme.shape.borderRadius,
    },
  })
);

interface Props {
  open: boolean;
  handleMenuOpen?: () => void;
  toggleTheme?: () => void;
  useDefaultTheme?: boolean;
  pageDisplayed: string;
}

const Header: React.FC<Props> = (
  { open, handleMenuOpen, toggleTheme, useDefaultTheme, pageDisplayed },
  props: { instance: any }
): ReactElement => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch: Dispatch<any> = useDispatch();

  const identityMgrType: IdentityManagerType = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.identityMgrType
  );

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const { logout } = useAuth0();

  const logoutClick = async () => {
    if (identityMgrType === IdentityManagerType.Auth0) {
      await logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }

    onLogout(dispatch);
  };

  const [settingsVisible, setSettingsVisible] = useState(false);

  const handleSettingsOpen = () => setSettingsVisible(true);

  const handleSettingClose = () => setSettingsVisible(false);

  const hiddenTitle = GetString('PAGE_TITLE_OVERVIEW');

  const shouldDisplayPageTitle = open || pageDisplayed !== hiddenTitle;

  return (
    <>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={2}
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open,
        })}
      >
        <Toolbar className={classes.toolbar}>
          <IconButton
            color="inherit"
            aria-label="open menu"
            onClick={handleMenuOpen}
            edge="start"
            className={clsx(classes.menuButton, classes.appBarIconButton, {
              [classes.hide]: open,
            })}
            size="medium"
          >
            <MenuIcon />
          </IconButton>
          <div className={classes.title}>
            {shouldDisplayPageTitle && (
                <PageTitle title={pageDisplayed} />
            )}
          </div>
          <div className={classes.search}>
            <div className={classes.searchIcon}>
              <IconSearch />
            </div>
            <InputBase
              placeholder={GetString('SEARCH_PLACEHOLDER')}
              classes={{
                root: classes.inputRoot,
                input: classes.inputInput,
              }}
              inputProps={{ 'aria-label': 'search' }}
            />
          </div>
          <IconButton
            className={classes.appBarIconButton}
            onClick={toggleTheme}
          >
            {useDefaultTheme ? (
              <EnterpriseTooltip
                title={GetString('TOOLTIP_DARK_MODE')}
                placement="bottom"
                TransitionComponent={Zoom}
              >
                <IconDarkTheme />
              </EnterpriseTooltip>
            ) : (
              <EnterpriseTooltip
                title={GetString('TOOLTIP_LIGHT_MODE')}
                placement="bottom"
                TransitionComponent={Zoom}
              >
                <IconLightTheme />
              </EnterpriseTooltip>
            )}
          </IconButton>
          <IconButton
            aria-label="settings"
            aria-controls="menu"
            aria-haspopup="true"
            onClick={handleSettingsOpen}
            className={classes.appBarIconButton}
          >
            <EnterpriseTooltip
              title={GetString('TOOLTIP_SETTINGS')}
              placement="bottom"
              TransitionComponent={Zoom}
            >
              <IconSettingsOutlined fontSize="inherit" />
            </EnterpriseTooltip>
          </IconButton>
          <IconButton
            aria-label="user account"
            aria-controls="menu"
            aria-haspopup="true"
            onClick={handleClick}
            className={classes.appBarIconButton}
          >
            <EnterpriseTooltip
              title={GetString('TOOLTIP_USER_ACCOUNT')}
              placement="bottom"
              TransitionComponent={Zoom}
            >
              <IconAccountCircleOutlined fontSize="inherit" />
            </EnterpriseTooltip>
          </IconButton>
          <Menu
            id="menu"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleClose}>{GetString('PROFILE')}</MenuItem>
            <MenuItem onClick={logoutClick}>{GetString('SIGN_OUT')}</MenuItem>
          </Menu>

          <SettingsVerticalTabs
            visible={settingsVisible}
            handleClose={handleSettingClose}
          />
        </Toolbar>
      </AppBar>
    </>
  );
};

export default Header;
